/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Wrench, Calendar, Clock, AlertTriangle,
  Users, TrendingUp, Euro, ChevronRight, UserX, Boxes, Phone
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import AnnullaTimerModal from '../components/AnnullaTimerModal';
import SessioneAdminModal from '../components/SessioneAdminModal';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../api/dashboard';
import { prodottiAPI } from '../api/prodotti';
import { sessioniAPI } from '../api/sessioni';
import { fmtDataOra, relativeTime } from '../utils/date';
import { fmtDurata, fmtDurataOre } from '../utils/durata';

const ACCENT = '#8B5CF6';

const STATO_LABELS = {
  in_attesa: 'In attesa', confermato: 'Confermati',
  in_corso: 'In corso', completato: 'Completati', annullato: 'Annullati',
};

const PRIORITA_STYLE = {
  urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' },
  alta:    { bg: '#FED7AA', color: '#9A3412', label: 'Alta' },
  normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' },
  bassa:   { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [timerDaAnnullare, setTimerDaAnnullare] = useState(null);
  const [timerDaTerminare, setTimerDaTerminare] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard-admin'],
    queryFn: () => dashboardAPI.admin(),
    refetchInterval: 60000, // ricarica ogni minuto
  });

  const { data: scadenze } = useQuery({
    queryKey: ['scadenze-admin'],
    queryFn: () => prodottiAPI.scadenzeAdmin(),
    refetchInterval: 60000,
  });

  const { data: timerAttivi = [] } = useQuery({
    queryKey: ['sessioni-attive'],
    queryFn: () => sessioniAPI.attive(),
    refetchInterval: 60000,
  });


  if (isLoading) {
    return <AdminLayout title="Dashboard"><div style={styles.loading}>Caricamento...</div></AdminLayout>;
  }

  if (error) {
    return (
      <AdminLayout title="Dashboard">
        <div style={styles.errorCard}>
          <p>Errore nel caricamento dei dati. Riprova ricaricando la pagina.</p>
        </div>
      </AdminLayout>
    );
  }

  const totaleInterventi = data.interventi_per_stato.reduce((acc, s) => acc + s.totale, 0);
  const interventiAttivi = data.interventi_per_stato
    .filter(s => !['completato', 'annullato'].includes(s.stato))
    .reduce((acc, s) => acc + s.totale, 0);

  return (
    <AdminLayout title="Dashboard">
      <div style={styles.welcome}>
        <h2 style={{ margin: 0 }}>Benvenuto, {user?.nome}</h2>
        <p style={{ color: '#6B7280', margin: '0.3rem 0 0' }}>
          Ecco un riassunto della giornata.
        </p>
      </div>

      {/* KPI principali */}
      <div style={styles.kpiGrid}>
        <KpiCard
          icon={Wrench}
          label="Interventi attivi"
          value={interventiAttivi}
          subtext={`su ${totaleInterventi} totali`}
          color="#8B5CF6"
          onClick={() => navigate('/interventi')}
        />
        <KpiCard
          icon={Calendar}
          label="Appuntamenti oggi"
          value={data.appuntamenti_oggi.length}
          color="#3B82F6"
          onClick={() => navigate('/interventi')}
        />
        <KpiCard
          icon={UserX}
          label="Da assegnare"
          value={data.interventi_non_assegnati.length}
          color={data.interventi_non_assegnati.length > 0 ? '#F59E0B' : '#10B981'}
          urgent={data.interventi_non_assegnati.length > 0}
          onClick={() => navigate('/interventi')}
        />
        <KpiCard
          icon={Clock}
          label="Ore mese"
          value={fmtDurataOre(data.ore_mese)}
          color="#10B981"
        />
        <KpiCard
          icon={Euro}
          label="Fatturato mese"
          value={`€ ${data.fatturato_mese.toFixed(2)}`}
          color="#059669"
          onClick={() => navigate('/report')}
        />
        <KpiCard
          icon={Users}
          label="Clienti / Tecnici"
          value={`${data.utenti.cliente} / ${data.utenti.tecnico}`}
          color="#6366F1"
          onClick={() => navigate('/utenti')}
        />
      </div>

      {/* Interventi per stato (chip cliccabili) */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Interventi per stato</h3>
        <div style={styles.stateChipsRow}>
          {data.interventi_per_stato.map((s) => (
            <button
              key={s.stato}
              onClick={() => navigate(`/interventi`)}
              style={{
                ...styles.stateChip,
                borderLeft: `4px solid ${s.colore}`,
              }}
            >
              <div style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>
                {STATO_LABELS[s.stato] || s.stato}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginTop: '0.2rem' }}>
                {s.totale}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div style={styles.twoColumns}>
        {/* Da assegnare */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <UserX size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Interventi da assegnare
            </h3>
            {data.interventi_non_assegnati.length > 0 && (
              <span style={styles.urgentBadge}>{data.interventi_non_assegnati.length}</span>
            )}
          </div>
          {data.interventi_non_assegnati.length === 0 ? (
            <div style={styles.emptyState}>
              ✓ Tutti gli interventi attivi hanno almeno un tecnico assegnato.
            </div>
          ) : (
            <div style={styles.itemsList}>
              {data.interventi_non_assegnati.slice(0, 5).map((i) => {
                const prio = PRIORITA_STYLE[i.priorita] || PRIORITA_STYLE.normale;
                return (
                  <div
                    key={i.id}
                    onClick={() => navigate(`/interventi/${i.id}`)}
                    style={styles.itemCard}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.itemHeader}>
                        <span style={{ ...styles.prioBadgeSm, backgroundColor: prio.bg, color: prio.color }}>
                          {prio.label}
                        </span>
                        <code style={styles.codeSm}>{i.codice}</code>
                      </div>
                      <div style={styles.itemTitle}>{i.titolo}</div>
                      <div style={styles.itemMeta}>
                        {i.cliente_id ? `${i.cliente_nome} ${i.cliente_cognome}` : 'Lavoro interno'} · creato {relativeTime(i.created_at)}
                      </div>
                    </div>
                    <ChevronRight size={18} color="#9CA3AF" />
                  </div>
                );
              })}
              {data.interventi_non_assegnati.length > 5 && (
                <button onClick={() => navigate('/interventi')} style={styles.seeAllBtn}>
                  Vedi tutti ({data.interventi_non_assegnati.length}) →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Appuntamenti oggi */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <Calendar size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Appuntamenti di oggi
            </h3>
          </div>
          {data.appuntamenti_oggi.length === 0 ? (
            <div style={styles.emptyState}>
              Nessun appuntamento programmato per oggi.
            </div>
          ) : (
            <div style={styles.itemsList}>
              {data.appuntamenti_oggi.map((i) => (
                <div
                  key={i.id}
                  onClick={() => navigate(`/interventi/${i.id}`)}
                  style={styles.itemCard}
                >
                  <div style={styles.timeChip}>
                    {new Date(i.data_appuntamento).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.itemHeader}>
                      <span style={{ ...styles.statoBadgeSm, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>
                        {STATO_LABELS[i.stato] || i.stato}
                      </span>
                      <code style={styles.codeSm}>{i.codice}</code>
                    </div>
                    <div style={styles.itemTitle}>{i.titolo}</div>
                    <div style={styles.itemMeta}>
                      {i.cliente_id ? `${i.cliente_nome} ${i.cliente_cognome}` : 'Lavoro interno'}
                      {i.citta && ` · ${i.citta}`}
                      {i.tecnici && i.tecnici.length > 0 && ` · ${i.tecnici.map(t => `${t.nome} ${t.cognome}`).join(', ')}`}
                    </div>
                  </div>
                  <ChevronRight size={18} color="#9CA3AF" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Timer attivi: serve ad accorgersi di quelli lasciati accesi */}
      {timerAttivi.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <Clock size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Timer attivi ({timerAttivi.length})
            </h3>
          </div>
          <div style={styles.itemsList}>
            {timerAttivi.map((s) => {
              const secondi = Math.max(0, Math.floor((Date.now() - new Date(s.inizio).getTime()) / 1000));
              const dimenticato = secondi >= 12 * 3600;
              return (
                <div key={s.id} style={styles.timerRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {capitalize(s.tecnico_nome)} {capitalize(s.tecnico_cognome)}
                    </div>
                    <div style={styles.itemMeta}>
                      {s.codice ? `${s.codice} · ${s.titolo}` : 'Lavoro libero'} · da {fmtDataOra(s.inizio)}
                    </div>
                  </div>
                  <div style={{
                    ...styles.durataBadge,
                    backgroundColor: dimenticato ? '#FEE2E2' : '#EDE9FE',
                    color: dimenticato ? '#991B1B' : '#5B21B6',
                  }}>
                    {dimenticato && <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />}
                    {fmtDurata(secondi, { compatto: true })}
                  </div>
                  <button onClick={() => setTimerDaTerminare(s)} style={styles.terminaBtn}>Termina</button>
                  <button onClick={() => setTimerDaAnnullare(s)} style={styles.annullaBtn}>Annulla</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {timerDaAnnullare && (
        <AnnullaTimerModal
          sessione={timerDaAnnullare}
          onClose={() => setTimerDaAnnullare(null)}
          onDone={() => {
            setTimerDaAnnullare(null);
            qc.invalidateQueries({ queryKey: ['sessioni-attive'] });
            qc.invalidateQueries({ queryKey: ['dashboard-admin'] });
          }}
        />
      )}

      {timerDaTerminare && (
        <SessioneAdminModal
          sessione={timerDaTerminare}
          modo="termina"
          onClose={() => setTimerDaTerminare(null)}
          onDone={() => {
            setTimerDaTerminare(null);
            qc.invalidateQueries({ queryKey: ['sessioni-attive'] });
            qc.invalidateQueries({ queryKey: ['dashboard-admin'] });
          }}
        />
      )}

      <div style={styles.twoColumns}>
        {/* Carico tecnici */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <Users size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Carico tecnici
            </h3>
          </div>
          {data.tecnici.length === 0 ? (
            <div style={styles.emptyState}>
              Nessun tecnico attivo. <span style={styles.linkSpan} onClick={() => navigate('/utenti')}>Crea il primo →</span>
            </div>
          ) : (
            <div style={styles.itemsList}>
              {data.tecnici.map((t) => (
                <div key={t.id} style={styles.tecnicoRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{capitalize(t.nome)} {capitalize(t.cognome)}</div>
                  </div>
                  <div style={{
                    ...styles.caricoBadge,
                    backgroundColor: t.interventi_attivi === 0 ? '#F3F4F6' : t.interventi_attivi >= 5 ? '#FEE2E2' : '#EDE9FE',
                    color: t.interventi_attivi === 0 ? '#6B7280' : t.interventi_attivi >= 5 ? '#991B1B' : '#5B21B6',
                  }}>
                    {t.interventi_attivi} {t.interventi_attivi === 1 ? 'intervento' : 'interventi'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scorte basse */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <AlertTriangle size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem', color: data.materiali_scarsi.length > 0 ? '#DC2626' : undefined }} />
              Scorte basse
            </h3>
            {data.materiali_scarsi.length > 0 && (
              <span style={styles.urgentBadge}>{data.materiali_scarsi.length}</span>
            )}
          </div>
          {data.materiali_scarsi.length === 0 ? (
            <div style={styles.emptyState}>
              ✓ Tutti i materiali sono sopra la soglia minima.
            </div>
          ) : (
            <div style={styles.itemsList}>
              {data.materiali_scarsi.map((m) => (
                <div key={m.id} style={styles.scorteRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{m.nome}</div>
                    {m.codice && <code style={styles.codeSm}>{m.codice}</code>}
                  </div>
                  <div style={styles.scorteValori}>
                    <span style={{ color: '#DC2626', fontWeight: 700, fontSize: '1.05rem' }}>
                      {parseFloat(m.quantita_magazzino)}
                    </span>
                    <span style={{ color: '#9CA3AF' }}> / {parseFloat(m.quantita_minima)} {m.unita_misura}</span>
                  </div>
                </div>
              ))}
              <button onClick={() => navigate('/materiali')} style={styles.seeAllBtn}>
                Gestisci magazzino →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Trend mini */}
      {data.trend_30gg.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              <TrendingUp size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Interventi creati (ultimi 30 giorni)
            </h3>
            <button onClick={() => navigate('/report')} style={styles.linkBtn}>
              Vai ai report →
            </button>
          </div>
          <MiniTrend data={data.trend_30gg} />
        </div>
      )}

      {scadenze && (
        <div style={styles.prodWidget}>
          <div style={styles.prodHeader}>
            <h3 style={styles.sectionTitle}><Boxes size={18} /> Prodotti e manutenzioni</h3>
            <button onClick={() => navigate('/prodotti')} style={styles.linkBtn}>Gestisci →</button>
          </div>
          <div style={styles.prodKpis}>
            <div style={styles.prodKpi}><div style={styles.prodKpiNum}>{scadenze.totale_prodotti}</div><div style={styles.prodKpiLabel}>Prodotti totali</div></div>
            <div style={{ ...styles.prodKpi, backgroundColor: '#FEF2F2' }}><div style={{ ...styles.prodKpiNum, color: '#DC2626' }}>{scadenze.scadute}</div><div style={styles.prodKpiLabel}>Scadute</div></div>
            <div style={{ ...styles.prodKpi, backgroundColor: '#FFFBEB' }}><div style={{ ...styles.prodKpiNum, color: '#D97706' }}>{scadenze.imminenti}</div><div style={styles.prodKpiLabel}>In scadenza</div></div>
          </div>
          {scadenze.lista.length > 0 ? (
            <div style={styles.scadList}>
              {scadenze.lista.slice(0, 8).map((s, idx) => {
                const col = s.colore === 'scaduta' ? '#DC2626' : s.colore === 'rosso' ? '#EF4444' : '#F59E0B';
                return (
                  <div key={idx} style={styles.scadRow}>
                    <div style={{ width: '6px', height: '38px', borderRadius: '3px', backgroundColor: col, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{s.cliente}</div>
                      <div style={styles.scadMeta}>{s.manutenzione} · {s.prodotto} {s.marca}</div>
                    </div>
                    {s.cliente_telefono && <a href={`tel:${s.cliente_telefono}`} style={styles.telLink}><Phone size={13} /> {s.cliente_telefono}</a>}
                    <div style={{ ...styles.scadGiorni, color: col }}>{s.colore === 'scaduta' ? 'Scaduta' : `${s.giorni_rimanenti}gg`}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.88rem' }}>Nessuna manutenzione in scadenza.</div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function KpiCard({ icon: Icon, label, value, subtext, unit, color, urgent, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.kpiCard,
        ...(urgent ? { borderColor: color, backgroundColor: '#FFFBEB' } : {}),
        ...(onClick ? { cursor: 'pointer' } : { cursor: 'default' }),
      }}
      disabled={!onClick}
    >
      <div style={{ ...styles.kpiIconBox, backgroundColor: color + '20', color }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={styles.kpiLabel}>{label}</div>
        <div style={styles.kpiValue}>
          {value}{unit && <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#6B7280', marginLeft: '0.2rem' }}>{unit}</span>}
        </div>
        {subtext && <div style={styles.kpiSubtext}>{subtext}</div>}
      </div>
    </button>
  );
}

function MiniTrend({ data }) {
  if (data.length === 0) return null;

  const max = Math.max(...data.map(d => d.totale), 1);

  return (
    <div style={styles.trendChart}>
      {data.map((d, idx) => {
        const heightPct = (d.totale / max) * 100;
        const day = new Date(d.giorno);
        return (
          <div key={idx} style={styles.trendBarWrap} title={`${day.toLocaleDateString('it-IT')}: ${d.totale} interventi`}>
            <div style={{ ...styles.trendBar, height: `${Math.max(heightPct, 4)}%`, backgroundColor: ACCENT }} />
            <div style={styles.trendLabel}>{day.getDate()}/{day.getMonth() + 1}</div>
          </div>
        );
      })}
    </div>
  );
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const styles = {
  prodWidget: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginTop: '1.25rem' },
  prodHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  prodKpis: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.1rem' },
  prodKpi: { padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '10px', textAlign: 'center' },
  prodKpiNum: { fontSize: '1.6rem', fontWeight: 700, color: '#111827' },
  prodKpiLabel: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem' },
  scadList: { display: 'flex', flexDirection: 'column', gap: '0.55rem' },
  scadRow: { display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  scadMeta: { fontSize: '0.82rem', color: '#6B7280', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  telLink: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.82rem', color: ACCENT, textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' },
  scadGiorni: { fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap', minWidth: '52px', textAlign: 'right' },
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  errorCard: { backgroundColor: 'white', padding: '2rem', borderRadius: '12px' },
  welcome: { marginBottom: '1.5rem' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.85rem', marginBottom: '1.5rem' },
  kpiCard: { display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '1.1rem', backgroundColor: 'white', border: '2px solid transparent', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', fontFamily: 'inherit', textAlign: 'left' },
  kpiIconBox: { width: '42px', height: '42px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: '0.78rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
  kpiValue: { fontSize: '1.55rem', fontWeight: 700, color: '#111827', marginTop: '0.15rem' },
  kpiSubtext: { fontSize: '0.78rem', color: '#9CA3AF', marginTop: '0.1rem' },

  section: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  sectionTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' },
  urgentBadge: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.15rem 0.6rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 700 },

  twoColumns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' },

  stateChipsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.6rem' },
  stateChip: { padding: '0.85rem 1rem', backgroundColor: '#F9FAFB', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },

  itemsList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  itemCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0.9rem', backgroundColor: '#F9FAFB', borderRadius: '8px', cursor: 'pointer' },
  itemHeader: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem', flexWrap: 'wrap' },
  itemTitle: { fontWeight: 600, fontSize: '0.92rem', color: '#111827' },
  itemMeta: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.15rem' },

  prioBadgeSm: { padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 },
  statoBadgeSm: { padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 },
  codeSm: { backgroundColor: '#E5E7EB', padding: '0.1rem 0.35rem', borderRadius: '3px', fontSize: '0.72rem', fontFamily: 'ui-monospace, monospace', color: '#374151' },

  timeChip: { padding: '0.4rem 0.7rem', backgroundColor: ACCENT + '15', color: ACCENT, borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 },

  tecnicoRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  caricoBadge: { padding: '0.25rem 0.65rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600 },
  timerRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  durataBadge: { padding: '0.25rem 0.65rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 },
  annullaBtn: { padding: '0.4rem 0.85rem', backgroundColor: 'white', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', flexShrink: 0 },
  terminaBtn: { padding: '0.4rem 0.85rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', flexShrink: 0 },

  scorteRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.85rem', backgroundColor: '#FEF3C7', borderRadius: '8px', borderLeft: '3px solid #DC2626' },
  scorteValori: { fontSize: '0.92rem' },

  emptyState: { padding: '1.5rem 1rem', textAlign: 'center', color: '#6B7280', fontSize: '0.9rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },

  seeAllBtn: { padding: '0.5rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: ACCENT, fontWeight: 600, fontSize: '0.85rem', textAlign: 'left' },
  linkBtn: { padding: '0.3rem 0.6rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: ACCENT, fontWeight: 600, fontSize: '0.85rem' },
  linkSpan: { color: ACCENT, cursor: 'pointer', fontWeight: 600 },

  trendChart: { display: 'flex', alignItems: 'flex-end', gap: '2px', height: '100px', padding: '0 0.25rem' },
  trendBarWrap: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', minWidth: '15px' },
  trendBar: { width: '100%', borderRadius: '3px 3px 0 0', minHeight: '4px' },
  trendLabel: { fontSize: '0.62rem', color: '#9CA3AF', whiteSpace: 'nowrap' },
};


